"use client";

import { useCallback, useRef } from "react";
import {
    moveBox,
    paintZIndex,
    toPx,
    transformToCss,
    type PxBox,
} from "@/features/lomdaEditorV2/lib/elements";
import type { LomdaScene } from "@/features/lomdaEditorV2/lib/lomdaTypes";
import type { SceneElement } from "@/features/lomdaEditorV2/lib/elements/types";
import {
    useActiveTextEditorId,
    useEditorMode,
    useLiveKind,
    useLiveTransformFor,
    useSelectedElementId,
    useUiActions,
} from "@/features/lomdaEditorV2/store";
import { useCanvasGesture, useInteractDraggable } from "./useCanvasGesture";
import { SelectionFrame } from "./SelectionFrame";
import { ElementView } from "./elements/ElementView";

interface Props {
    scene: LomdaScene;
    element: SceneElement;
    canvasRef: React.RefObject<HTMLElement>;
}

const CLICK_MOVE_THRESHOLD = 4;

export function CanvasElement({ scene, element, canvasRef }: Props) {
    const mode = useEditorMode();
    const selectedElementId = useSelectedElementId();
    const activeTextEditorId = useActiveTextEditorId();
    const liveTransform = useLiveTransformFor(element.id);
    const liveKind = useLiveKind();
    const { selectElement, setActiveTextEditorId, enterTextEdit } =
        useUiActions();

    const interactive = mode !== "render" && !element.locked;
    const isSelected = selectedElementId === element.id;
    const isText = element.kind === "text";
    const isEditing = isText && activeTextEditorId === element.id;

    const effectiveTransform = liveTransform ?? element.transform;

    const boxRef = useRef<HTMLDivElement>(null);
    const downRef = useRef<{
        x: number;
        y: number;
        onText: boolean;
    } | null>(null);

    const getCanvasRect = useCallback(
        () => canvasRef.current?.getBoundingClientRect() ?? null,
        [canvasRef]
    );
    const getSiblingBoxes = useCallback(
        (): PxBox[] =>
            (scene.elements ?? [])
                .filter((e) => e.id !== element.id)
                .map((e) => toPx(e.transform)),
        [scene.elements, element.id]
    );
    const controller = useCanvasGesture(
        scene.id,
        element.id,
        effectiveTransform,
        getCanvasRect,
        getSiblingBoxes
    );

    // The element drags from anywhere on its body. While editing a text box we
    // additionally ignore the prose content so dragging there selects text
    // (and the box still drags from its empty padding).
    const ignoreFrom =
        isText && isEditing
            ? "[data-canvas-handle], .ProseMirror"
            : "[data-canvas-handle]";

    useInteractDraggable(
        boxRef,
        interactive,
        controller.start,
        (e) =>
            controller.update(
                (w) => moveBox(w.box, e.dx * w.scale, e.dy * w.scale),
                { kind: "move", disable: e.altKey }
            ),
        controller.end,
        ignoreFrom
    );

    const isOnText = (target: EventTarget | null) =>
        target instanceof HTMLElement && !!target.closest(".ProseMirror");

    const handlePointerDown = (e: React.PointerEvent) => {
        if (!interactive) return;
        e.stopPropagation();
        downRef.current = {
            x: e.clientX,
            y: e.clientY,
            onText: isOnText(e.target),
        };
        if (!isSelected) selectElement(element.id);
    };

    // A click (no drag) decides the mode for a text box: on the text → edit
    // with the caret at the click point; on empty padding → just selected
    // (drag mode). A real drag is handled by interact.js and skipped here.
    const handlePointerUp = (e: React.PointerEvent) => {
        const down = downRef.current;
        downRef.current = null;
        if (!down || !isText) return;
        const moved =
            Math.hypot(e.clientX - down.x, e.clientY - down.y) >
            CLICK_MOVE_THRESHOLD;
        if (moved) return;
        if (down.onText) {
            if (!isEditing)
                enterTextEdit(element.id, { x: e.clientX, y: e.clientY });
        } else if (isEditing) {
            setActiveTextEditorId(null);
        }
    };

    // Double-click anywhere on a text box enters edit mode — works even on the
    // empty padding (and for empty content, where there's no glyph to click).
    const handleDoubleClick = (e: React.MouseEvent) => {
        if (!interactive || !isText || isEditing) return;
        enterTextEdit(element.id, { x: e.clientX, y: e.clientY });
    };

    // Inline cursor so it wins deterministically and inherits into the
    // element's content. While editing, the prose shows its native text caret.
    const cursor = !interactive ? undefined : isEditing ? "default" : "move";

    return (
        <div
            ref={boxRef}
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onDoubleClick={handleDoubleClick}
            style={{
                ...transformToCss(effectiveTransform),
                zIndex: paintZIndex(element),
                cursor,
            }}
            className="touch-none"
        >
            <div className="relative h-full w-full">
                <ElementView
                    scene={scene}
                    element={element}
                    isEditing={isEditing}
                />
                {interactive && isSelected && (
                    <SelectionFrame
                        controller={controller}
                        rotation={effectiveTransform.rotation}
                        showAngle={liveKind === "rotate" && liveTransform !== null}
                    />
                )}
            </div>
        </div>
    );
}
